import { Component, OnInit, OnDestroy } from '@angular/core';
import { RoleDTO, UserDTO } from '@lkp/parking-sdk';
import { Subject, takeUntil, debounceTime, distinctUntilChanged } from 'rxjs';
import { EditUserComponent } from 'src/app/components/edit-user/edit-user.component';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngxs/store';
import { UsersState } from 'src/app/core/states/users/users.state';
import {
  AddUser,
  GetRoles,
  GetUsers,
} from 'src/app/core/states/users/user.actions';
import { ErrorDialogComponent } from 'src/app/components/error-dialog/error-dialog.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FormControl } from '@angular/forms';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';

@Component({
    selector: 'app-user-administration',
    templateUrl: './user-administration.component.html',
    styleUrls: ['./user-administration.component.scss'],
    standalone: false
})
export class UserAdministrationComponent implements OnInit, OnDestroy {
  users: UserDTO[] = [];
  roles: RoleDTO[] = [];

  // Paginator
  pageIndex: number = 0;
  pageSize: number = 50;
  pageSizeOptions: number[] = [10, 20, 50, 100];
  totalUsers: number = 0;

  // Sorting
  sortColumn: string = 'username';
  sortDirection: 'asc' | 'desc' = 'asc';

  // Searching & Filtering
  searchInput = new FormControl('');
  roleFilter: string = 'ALL';

  readonly onDestroy$ = new Subject<void>();

  constructor(
    readonly store: Store,
    readonly dialog: MatDialog,
    readonly snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.store
      .select(UsersState.currentUsers)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((users) => {
        this.users = users;
      });

    this.store
      .select(UsersState.totalUsers)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((total) => {
        this.totalUsers = total || 0;
      });

    this.store
      .select(UsersState.currentRoles)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((roles) => {
        this.roles = roles;
      });

    // Load roles
    this.store.dispatch(new GetRoles());

    this.loadUsers();

    this.searchInput.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.onDestroy$)
      )
      .subscribe(() => {
        this.pageIndex = 0;
        this.loadUsers();
      });
  }

  // Loads users with current filters
  private loadUsers(): void {
    const roleName = this.roleFilter === 'ALL' ? undefined : this.roleFilter;

    this.store.dispatch(
      new GetUsers(
        this.pageIndex,
        this.pageSize,
        this.searchInput.value?.trim() || undefined,
        roleName,
        this.sortColumn,
        this.sortDirection
      )
    );
  }

  applyRoleFilter(): void {
    this.pageIndex = 0;
    this.loadUsers();
  }

  paginator(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadUsers();
  }

  sortColumns(event: Sort): void {
    this.sortColumn = event.active;
    this.sortDirection = (event.direction as 'asc' | 'desc') || 'asc';
    this.pageIndex = 0;
    this.loadUsers();
  }

  openEditDialog(user?: UserDTO): void {
    const dialogRef = this.dialog.open(EditUserComponent, {
      width: '600px',
      data: user ? { user } : undefined,
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((result) => {
        if (!result) return;

        // Create user
        if (result.username && !user) {
          // Use the values from the dialog result to create a new user
          const newUser: UserDTO = {
            username: result.username,
            name: result.name,
            roles: result.roles,
            newPassword: result.newPassword?.trim() || undefined,
          };
          this.store
            .dispatch(new AddUser(newUser))
            .pipe(takeUntil(this.onDestroy$))
            .subscribe({
              next: () =>
                this.snackBar.open('Ny användare skapad', 'OK', {
                  duration: 3000,
                }),
              error: () =>
                this.dialog.open(ErrorDialogComponent, {
                  data: {
                    title: 'Det gick inte att skapa användaren',
                    message: ['Var god prova igen om en liten stund.'],
                  },
                }),
            });
          return;
        }
      });
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
